/**
 * Portfolio Analyzer Page - Auxilio Finance
 * AI-powered investment portfolio analysis and fee identification
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChartPieIcon,
  PlusIcon,
  SparklesIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  CalculatorIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface AssetClass {
  id: string;
  name: string;
}

interface Holding {
  symbol?: string;
  name: string;
  asset_class: string;
  value: number;
  shares?: number;
  cost_basis?: number;
  expense_ratio?: number;
  account_type?: string;
}

interface FeeBreakdown {
  name: string;
  symbol?: string;
  value: number;
  expense_ratio: number;
  annual_fee: number;
}

interface AnalysisResult {
  id: string;
  analysis_date: string;
  total_value: number;
  holdings_count: number;
  allocation: {
    allocation_values: Record<string, number>;
    allocation_percentages: Record<string, number>;
    concentration_warnings: string[];
  };
  risk_profile: {
    id: string;
    name: string;
    description: string;
  };
  fees: {
    total_annual_fees: number;
    effective_rate: number;
    fee_breakdown: FeeBreakdown[];
    projected_10yr_fees: number;
    fee_drag_10yr: number;
    warnings: string[];
    hidden_fees: string[];
  };
  insights: string[];
  recommendations: string[];
  warnings: string[];
  score: number;
  score_breakdown: {
    diversification: number;
    fees: number;
    risk_alignment: number;
  };
}

interface RiskProfile {
  id: string;
  name: string;
  description: string;
  stocks: number;
  bonds: number;
  cash: number;
}

const ALLOCATION_COLORS: Record<string, string> = {
  stocks: '#10b981',
  bonds: '#3b82f6',
  cash: '#6b7280',
  real_estate: '#f59e0b',
  commodities: '#ef4444',
  crypto: '#8b5cf6',
  alternatives: '#ec4899',
  other: '#14b8a6',
};

export const PortfolioAnalyzer: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [activeTab, setActiveTab] = useState<'holdings' | 'analysis' | 'rebalance'>('holdings');
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [riskProfiles, setRiskProfiles] = useState<RiskProfile[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });

  // Form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [holdingForm, setHoldingForm] = useState<Holding>({
    name: '',
    asset_class: 'stocks',
    value: 0,
    symbol: '',
    expense_ratio: undefined,
  });

  // Rebalance state
  const [selectedRiskProfile, setSelectedRiskProfile] = useState<string>('moderate');
  const [rebalanceResult, setRebalanceResult] = useState<any>(null);

  // File upload ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadReferenceData();
    loadSavedPortfolio();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [assetRes, riskRes] = await Promise.all([
        apiClient.get('/portfolio-analyzer/reference/asset-classes'),
        apiClient.get('/portfolio-analyzer/reference/risk-profiles'),
      ]);
      setAssetClasses(assetRes.data.asset_classes);
      setRiskProfiles(riskRes.data.profiles);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const loadSavedPortfolio = async () => {
    try {
      const response = await apiClient.get('/portfolio-analyzer/my-portfolio');
      if (response.data.found) {
        setHoldings(response.data.holdings || []);
        if (response.data.analysis) {
          setAnalysis(response.data.analysis);
        }
      }
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  };

  const lookupFund = async (symbol: string) => {
    try {
      const response = await apiClient.get(`/portfolio-analyzer/fund-lookup/${symbol}`);
      if (response.data.found) {
        setHoldingForm((prev) => ({
          ...prev,
          name: response.data.name || prev.name,
          expense_ratio: response.data.expense_ratio,
          asset_class: response.data.asset_class || prev.asset_class,
        }));
      }
    } catch (err) {
      console.error('Fund lookup failed:', err);
    }
  };

  const handleAddHolding = () => {
    if (!holdingForm.name || holdingForm.value <= 0) {
      setError(t('portfolioAnalyzer:errors.invalidHolding', 'Please enter a valid holding name and value'));
      return;
    }

    if (editingIndex !== null) {
      const updated = [...holdings];
      updated[editingIndex] = holdingForm;
      setHoldings(updated);
    } else {
      setHoldings([...holdings, holdingForm]);
    }

    setShowAddModal(false);
    setEditingIndex(null);
    resetForm();
    setAnalysis(null); // Clear analysis when holdings change
  };

  const handleEditHolding = (index: number) => {
    setHoldingForm(holdings[index]);
    setEditingIndex(index);
    setShowAddModal(true);
  };

  const handleDeleteHolding = (index: number) => {
    if (window.confirm(t('portfolioAnalyzer:confirmDelete', 'Are you sure you want to delete this holding?'))) {
      const updated = holdings.filter((_, i) => i !== index);
      setHoldings(updated);
      setAnalysis(null);
    }
  };

  const resetForm = () => {
    setHoldingForm({
      name: '',
      asset_class: 'stocks',
      value: 0,
      symbol: '',
      expense_ratio: undefined,
    });
  };

  const handleAnalyze = async () => {
    if (holdings.length === 0) {
      setError(t('portfolioAnalyzer:errors.noHoldings', 'Please add at least one holding to analyze'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/portfolio-analyzer/analyze', {
        holdings: holdings,
        language: 'en',
      });
      setAnalysis(response.data);
      setActiveTab('analysis');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('portfolioAnalyzer:errors.analyzeFailed', 'Failed to analyze portfolio'));
    } finally {
      setLoading(false);
    }
  };

  const handleRebalance = async () => {
    const selectedProfile = riskProfiles.find((p) => p.id === selectedRiskProfile);
    if (!selectedProfile) return;

    setLoading(true);
    try {
      const response = await apiClient.post('/portfolio-analyzer/rebalance', {
        holdings: holdings,
        targets: [
          { asset_class: 'stocks', target_percent: selectedProfile.stocks },
          { asset_class: 'bonds', target_percent: selectedProfile.bonds },
          { asset_class: 'cash', target_percent: selectedProfile.cash },
        ],
        language: 'en',
      });
      setRebalanceResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to calculate rebalancing');
    } finally {
      setLoading(false);
    }
  };

  const handleClearPortfolio = async () => {
    if (window.confirm(t('portfolioAnalyzer:confirmClear', 'Are you sure you want to clear your entire portfolio?'))) {
      setHoldings([]);
      setAnalysis(null);
      setRebalanceResult(null);
      try {
        await apiClient.delete('/portfolio-analyzer/my-portfolio');
      } catch (err) {
        console.error('Failed to clear portfolio on server:', err);
      }
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('portfolioAnalyzer:importing', 'Importing Portfolio Data...'),
      subMessage: isPdf
        ? t('portfolioAnalyzer:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('portfolioAnalyzer:importingCsvHint', 'Analyzing and extracting portfolio information')
    });
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', 'en');

      const response = await apiClient.post('/portfolio-analyzer/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.holdings && response.data.holdings.length > 0) {
        setHoldings(response.data.holdings);
        // The upload endpoint also returns analysis results
        setAnalysis(response.data);
        setRebalanceResult(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('portfolioAnalyzer:errors.uploadFailed', 'Failed to upload and parse file'));
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = () => {
    if (holdings.length === 0) return;

    const headers = ['Symbol', 'Name', 'Asset Class', 'Value', 'Expense Ratio'];
    const rows = holdings.map(h => [
      h.symbol || '',
      h.name,
      h.asset_class,
      h.value.toString(),
      h.expense_ratio?.toString() || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `portfolio_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const generateExportContent = () => {
    const total = holdings.reduce((sum, h) => sum + h.value, 0);
    let content = `Portfolio Analysis Summary\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;

    content += `=== PORTFOLIO OVERVIEW ===\n`;
    content += `Total Value: ${formatCurrency(total)}\n`;
    content += `Number of Holdings: ${holdings.length}\n\n`;

    content += `=== HOLDINGS ===\n`;
    holdings.forEach(h => {
      content += `\n${h.name}${h.symbol ? ` (${h.symbol})` : ''}\n`;
      content += `  Asset Class: ${h.asset_class}\n`;
      content += `  Value: ${formatCurrency(h.value)}\n`;
      if (h.expense_ratio !== undefined) content += `  Expense Ratio: ${h.expense_ratio}%\n`;
    });

    if (analysis) {
      content += `\n=== ANALYSIS RESULTS ===\n`;
      content += `Risk Profile: ${analysis.risk_profile?.name || 'N/A'}\n`;
      content += `Portfolio Score: ${analysis.score}/100\n\n`;

      if (analysis.insights?.length) {
        content += `Insights:\n`;
        analysis.insights.forEach(i => content += `- ${i}\n`);
      }

      if (analysis.recommendations?.length) {
        content += `\nRecommendations:\n`;
        analysis.recommendations.forEach(r => content += `- ${r}\n`);
      }
    }

    return content;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    try {
      const content = generateExportContent();
      const response = await apiClient.post('/export/document', {
        content,
        title: 'Portfolio Analysis Summary',
        format,
      }, {
        responseType: 'blob',
      });

      const extension = format === 'word' ? 'docx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_portfolio_${new Date().toISOString().slice(0, 10)}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export document');
    }
  };

  const handlePrint = () => {
    const content = generateExportContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Portfolio Analysis Summary</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; white-space: pre-wrap; }
              h1 { color: #10b981; }
            </style>
          </head>
          <body>
            <h1>Portfolio Analysis Summary</h1>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const totalValue = holdings.reduce((sum, h) => sum + h.value, 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Prepare chart data
  const allocationChartData = analysis
    ? Object.entries(analysis.allocation.allocation_percentages)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => ({
          name: key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: value,
          color: ALLOCATION_COLORS[key] || '#6b7280',
        }))
    : [];

  const feeChartData = analysis
    ? analysis.fees.fee_breakdown.slice(0, 5).map((f) => ({
        name: f.symbol || f.name.substring(0, 10),
        fee: f.annual_fee,
        rate: f.expense_ratio,
      }))
    : [];

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChartPieIcon className="h-8 w-8 text-emerald-500" />
            {t('portfolioAnalyzer:title', 'Portfolio Analyzer')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('portfolioAnalyzer:subtitle', 'Analyze your investment portfolio, identify fees, and optimize allocation')}
          </p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Import/Export/Clear */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,.csv,text/csv,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const ext = file.name.toLowerCase().split('.').pop();
                  if (!['csv', 'pdf', 'txt'].includes(ext || '')) {
                    alert('Please select a CSV, PDF, or TXT file.');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                }
                handleFileUpload(e);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('portfolioAnalyzer:uploadPortfolio', 'Import Portfolio (CSV/PDF/TXT)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              onExportCSV={handleExportCSV}
              showCSV={true}
              showPrint={true}
            />
            <button
              onClick={handleClearPortfolio}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('portfolioAnalyzer:clearPortfolio', 'Clear All Data')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          {holdings.length > 0 && (
            <Button onClick={handleAnalyze} disabled={loading}>
              <SparklesIcon className="h-5 w-5 mr-1" />
              {t('portfolioAnalyzer:analyze', 'Analyze Portfolio')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Bar */}
      {holdings.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:totalValue', 'Total Value')}</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(totalValue)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:holdings', 'Holdings')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{holdings.length}</p>
            </div>
            {analysis && (
              <>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:annualFees', 'Annual Fees')}</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency(analysis.fees.total_annual_fees)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:effectiveRate', 'Effective Fee Rate')}</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatPercent(analysis.fees.effective_rate)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('holdings')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'holdings'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          {t('portfolioAnalyzer:tabs.holdings', 'Holdings')} ({holdings.length})
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'analysis'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
          disabled={!analysis}
        >
          <span className="flex items-center gap-1">
            <SparklesIcon className="h-4 w-4" />
            {t('portfolioAnalyzer:tabs.analysis', 'Analysis')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('rebalance')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'rebalance'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
          disabled={holdings.length === 0}
        >
          <span className="flex items-center gap-1">
            <ScaleIcon className="h-4 w-4" />
            {t('portfolioAnalyzer:tabs.rebalance', 'Rebalance')}
          </span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common:buttons.ok', 'OK')}
          </button>
        </div>
      )}

      {/* Holdings Tab */}
      {activeTab === 'holdings' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('portfolioAnalyzer:yourHoldings', 'Your Holdings')}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              {t('portfolioAnalyzer:addHolding', 'Add Holding')}
            </Button>
          </div>

          {holdings.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('portfolioAnalyzer:holdingName', 'Name')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('portfolioAnalyzer:assetClass', 'Asset Class')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('portfolioAnalyzer:value', 'Value')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('portfolioAnalyzer:expenseRatio', 'Expense Ratio')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('portfolioAnalyzer:allocation', '% of Portfolio')}
                    </th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {holdings.map((holding, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{holding.name}</p>
                          {holding.symbol && <p className="text-sm text-gray-500 dark:text-gray-400">{holding.symbol}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-1 text-xs rounded-full"
                          style={{
                            backgroundColor: `${ALLOCATION_COLORS[holding.asset_class] || '#6b7280'}20`,
                            color: ALLOCATION_COLORS[holding.asset_class] || '#6b7280',
                          }}
                        >
                          {holding.asset_class.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        {formatCurrency(holding.value)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                        {holding.expense_ratio !== undefined ? `${holding.expense_ratio}%` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                        {totalValue > 0 ? formatPercent((holding.value / totalValue) * 100) : '0%'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => handleEditHolding(index)}
                            className="p-1.5 text-gray-500 hover:text-emerald-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteHolding(index)}
                            className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-12 border border-gray-200 dark:border-gray-700 text-center">
              <ChartPieIcon className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('portfolioAnalyzer:noHoldings', 'No holdings added yet. Add your first investment to get started.')}
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                {t('portfolioAnalyzer:addFirstHolding', 'Add Your First Holding')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Analysis Tab */}
      {activeTab === 'analysis' && analysis && (
        <div className="space-y-6">
          {/* Score Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-6">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white ${getScoreBg(analysis.score)}`}>
                {analysis.score}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('portfolioAnalyzer:portfolioScore', 'Portfolio Health Score')}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:diversification', 'Diversification')}</p>
                    <p className={`text-xl font-bold ${getScoreColor(analysis.score_breakdown.diversification)}`}>
                      {analysis.score_breakdown.diversification}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:feeEfficiency', 'Fee Efficiency')}</p>
                    <p className={`text-xl font-bold ${getScoreColor(analysis.score_breakdown.fees)}`}>
                      {analysis.score_breakdown.fees}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('portfolioAnalyzer:riskAlignment', 'Risk Alignment')}</p>
                    <p className={`text-xl font-bold ${getScoreColor(analysis.score_breakdown.risk_alignment)}`}>
                      {analysis.score_breakdown.risk_alignment}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Allocation & Fees */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Allocation Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('portfolioAnalyzer:assetAllocation', 'Asset Allocation')}
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={allocationChartData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} dataKey="value">
                    {allocationChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {allocationChartData.map((item, index) => (
                  <span key={index} className="flex items-center gap-1 text-xs">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    {item.name}: {item.value.toFixed(1)}%
                  </span>
                ))}
              </div>

              {/* Risk Profile */}
              <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('portfolioAnalyzer:riskProfile', 'Risk Profile')}</p>
                <p className="font-semibold text-emerald-700 dark:text-emerald-300">{analysis.risk_profile.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{analysis.risk_profile.description}</p>
              </div>
            </div>

            {/* Fee Analysis */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('portfolioAnalyzer:feeAnalysis', 'Fee Analysis')}
              </h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('portfolioAnalyzer:annualFees', 'Annual Fees')}</p>
                  <p className="text-xl font-bold text-red-600 dark:text-red-400">{formatCurrency(analysis.fees.total_annual_fees)}</p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('portfolioAnalyzer:tenYearFeeDrag', '10-Year Fee Drag')}</p>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(analysis.fees.fee_drag_10yr)}</p>
                </div>
              </div>

              {/* Top Fee Holdings */}
              {feeChartData.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('portfolioAnalyzer:highestFees', 'Highest Fee Holdings')}</p>
                  {feeChartData.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{item.name}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(item.fee)}/yr ({item.rate}%)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Insights & Recommendations */}
          {analysis.insights && analysis.insights.length > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
              <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 mb-3 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5" />
                {t('portfolioAnalyzer:insights', 'AI Insights')}
              </h3>
              <ul className="space-y-2">
                {analysis.insights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-2 text-emerald-700 dark:text-emerald-300">
                    <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
                {t('portfolioAnalyzer:recommendations', 'Recommendations')}
              </h3>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3 text-blue-700 dark:text-blue-300">
                    <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {analysis.warnings && analysis.warnings.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5" />
                {t('portfolioAnalyzer:warnings', 'Warnings')}
              </h3>
              <ul className="space-y-2">
                {analysis.warnings.map((warning, index) => (
                  <li key={index} className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                    <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Rebalance Tab */}
      {activeTab === 'rebalance' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('portfolioAnalyzer:selectRiskProfile', 'Select Target Risk Profile')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              {riskProfiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => setSelectedRiskProfile(profile.id)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedRiskProfile === profile.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-emerald-300'
                  }`}
                >
                  <p className="font-medium text-gray-900 dark:text-white">{profile.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{profile.description}</p>
                  <div className="mt-2 text-xs">
                    <span className="text-green-600">{profile.stocks}% Stocks</span>
                    {' • '}
                    <span className="text-blue-600">{profile.bonds}% Bonds</span>
                  </div>
                </button>
              ))}
            </div>

            <Button onClick={handleRebalance} disabled={loading}>
              <CalculatorIcon className="h-5 w-5 mr-1" />
              {t('portfolioAnalyzer:calculateRebalance', 'Calculate Rebalancing')}
            </Button>
          </div>

          {rebalanceResult && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('portfolioAnalyzer:rebalanceActions', 'Rebalancing Actions')}
              </h3>

              {rebalanceResult.rebalance_actions.length > 0 ? (
                <div className="space-y-3">
                  {rebalanceResult.rebalance_actions.map((action: any, index: number) => (
                    <div key={index} className={`p-4 rounded-lg ${action.action === 'buy' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {action.action === 'buy' ? (
                            <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <ArrowTrendingDownIcon className="h-5 w-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {action.action.toUpperCase()} {action.asset_class.replace('_', ' ').toUpperCase()}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Current: {action.current_percent.toFixed(1)}% → Target: {action.target_percent.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <span className={`text-lg font-bold ${action.action === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(action.difference_value)}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="font-medium text-blue-800 dark:text-blue-200">{t('portfolioAnalyzer:notes', 'Notes')}</p>
                    <ul className="mt-2 space-y-1">
                      {rebalanceResult.notes.map((note: string, index: number) => (
                        <li key={index} className="text-sm text-blue-700 dark:text-blue-300">
                          • {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">{t('portfolioAnalyzer:noRebalanceNeeded', 'Your portfolio is already well-aligned with the target allocation!')}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Holding Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingIndex !== null ? t('portfolioAnalyzer:editHolding', 'Edit Holding') : t('portfolioAnalyzer:addHolding', 'Add Holding')}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingIndex(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('portfolioAnalyzer:symbol', 'Symbol (optional)')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={holdingForm.symbol || ''}
                    onChange={(e) => setHoldingForm({ ...holdingForm, symbol: e.target.value.toUpperCase() })}
                    placeholder="e.g., VTI, SPY"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={() => holdingForm.symbol && lookupFund(holdingForm.symbol)}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Lookup
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('portfolioAnalyzer:holdingName', 'Name')} *
                </label>
                <input
                  type="text"
                  value={holdingForm.name}
                  onChange={(e) => setHoldingForm({ ...holdingForm, name: e.target.value })}
                  placeholder="e.g., Vanguard Total Stock Market ETF"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('portfolioAnalyzer:assetClass', 'Asset Class')} *
                </label>
                <select
                  value={holdingForm.asset_class}
                  onChange={(e) => setHoldingForm({ ...holdingForm, asset_class: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {assetClasses.map((ac) => (
                    <option key={ac.id} value={ac.id}>
                      {ac.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('portfolioAnalyzer:value', 'Current Value')} *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    value={holdingForm.value || ''}
                    onChange={(e) => setHoldingForm({ ...holdingForm, value: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('portfolioAnalyzer:expenseRatio', 'Expense Ratio (%)')}
                </label>
                <input
                  type="number"
                  value={holdingForm.expense_ratio ?? ''}
                  onChange={(e) => setHoldingForm({ ...holdingForm, expense_ratio: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="e.g., 0.03"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="0"
                  max="10"
                  step="0.01"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingIndex(null);
                  resetForm();
                }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddHolding} fullWidth disabled={!holdingForm.name || holdingForm.value <= 0}>
                {editingIndex !== null ? t('common:buttons.save', 'Save') : t('portfolioAnalyzer:addHolding', 'Add Holding')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('common:disclaimer.financial', 'This information is for educational purposes only and is not financial advice. Please consult a qualified financial professional for personalized guidance.')}
        </p>
      </div>
    </div>
  );
};

export default PortfolioAnalyzer;
